import { Injectable, Type } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject } from 'rxjs';
import { PopUpComponent } from '../components/pop-up/pop-up.component';
import { PopUpHtmlComponent } from '../components/pop-up-html/pop-up-html.component';
import { ToastService } from './toast.service';

/** Wariant pop-upu z jednym przyciskiem OK. Dwójka to „tak / anuluj", trójka — okno wyboru. */
const SINGLE_OK_BUTTON = 1;

/**
 * Z tego pop-upu nie ma wyjścia innego niż OK: klik w tło i Esc są wyłączone, a przycisk X
 * rysuje się tylko dla wariantu 3. Na tym opiera się `present()` — zamknięcie bez OK jest
 * dla niego stanem nieosiągalnym z interfejsu, a nie ścieżką, którą trzeba obsłużyć.
 * Poluzowanie którejkolwiek z tych opcji przewraca to założenie.
 */
const CONFIRMATION_MODAL: NgbModalOptions = { centered: true, backdrop: 'static', keyboard: false };

/** Czego serwis wymaga od komponentu wyświetlającego potwierdzenie. */
interface ConfirmationPopUp {
  message: string;
  modalType: number;
  eventId: string;
}

/**
 * Potwierdzenia wykonanych akcji — „Zapisano", „Usunięto użytkownika", „Wizyta została dodana".
 *
 * Wcześniej szły tą samą metodą co komunikaty o błędach (ModalService.open), więc nie dało się
 * zmienić prezentacji potwierdzeń bez ruszania błędów, ani nawet ich w kodzie odnaleźć.
 *
 * Metody publiczne mówią, CO się stało; prywatne present() decyduje, JAK to pokazać. Żeby
 * zamienić modal na tosta w całej aplikacji, wystarczy zmienić present() — wywołania zostają.
 *
 * Nie należą tu: komunikaty o błędach (ModalService.open z flagą error), okna z wyborem
 * (ModalService.openAccept / openChoice) i pop-upy alarmowe.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmationService {

  constructor(
    private modal: NgbModal,
    private translate: TranslateService,
    private router: Router,
    private toast: ToastService
  ) { }

  /**
   * Potwierdzenie wykonanej akcji.
   *
   * Zwrócony strumień emituje po zamknięciu pop-upu — subskrybuj tylko wtedy, gdy coś ma się
   * stać potem (np. przeładowanie widoku). Do przejścia na inny ekran jest showAndNavigate().
   */
  show(messageKey: string, params?: object, eventId?: string): Observable<void> {
    return this.present(this.translate.instant(messageKey, params), PopUpComponent, eventId);
  }

  /** Potwierdzenie, po którym użytkownik trafia na inny ekran — dopiero po kliknięciu OK. */
  showAndNavigate(messageKey: string, route: string, params?: object, eventId?: string): void {
    this.show(messageKey, params, eventId).subscribe(() => this.router.navigateByUrl(route));
  }

  /**
   * To samo co show(), ale bez blokowania ekranu — komunikat leci toastem i znika sam.
   *
   * Rusztowanie na czas przechodzenia z modali na toasty ekran po ekranie. Gdy wszystkie
   * wywołania się przeniosą, `show` i `showAndNavigate` znikną, a warianty toastowe
   * przejmą ich nazwy — dopóki trwa migracja, obie pary muszą istnieć obok siebie.
   */
  showToast(messageKey: string, params?: object): void {
    this.toast.show(this.translate.instant(messageKey, params));
  }

  /** Niepowodzenie, które użytkownik ma przeczytać, ale które nie wymaga od niego decyzji. */
  showErrorToast(messageKey: string, params?: object): void {
    this.toast.showError(this.translate.instant(messageKey, params));
  }

  /**
   * Toast plus natychmiastowe przejście na inny ekran. Toast żyje poza <router-outlet>,
   * więc przeżywa zmianę trasy i towarzyszy użytkownikowi już na nowym ekranie.
   */
  showToastAndNavigate(messageKey: string, route: string, params?: object): void {
    this.showToast(messageKey, params);
    this.router.navigateByUrl(route);
  }

  /**
   * Potwierdzenie o treści HTML — kreatory składają komunikat z kilku tłumaczeń i nagłówka,
   * więc tekst przychodzi tu gotowy.
   *
   * Odpowiednika showAndNavigate nie ma: przejście na inny ekran robi po tym potwierdzeniu
   * jedno wywołanie w całej aplikacji i subskrypcja wystarcza.
   */
  showHtml(html: string, eventId?: string): Observable<void> {
    return this.present(html, PopUpHtmlComponent, eventId);
  }

  /** Jedyne miejsce, które decyduje, jak wygląda potwierdzenie. */
  private present(message: string, component: Type<ConfirmationPopUp>, eventId: string): Observable<void> {
    // ReplaySubject, nie Subject: wywołujący subskrybuje dopiero po powrocie z tej metody.
    // Dziś emisja i tak przychodzi później (klik w OK), ale prezentacja, która potwierdza
    // się sama — toast — zgłosiłaby zamknięcie natychmiast i zwykły Subject by ją zgubił,
    // cicho zabierając nawigację wszystkim wywołaniom showAndNavigate.
    const closed = new ReplaySubject<void>(1);

    // setTimeout jak w ModalService.open(): wywołania idą z callbacków subscribe, czyli ze
    // środka cyklu detekcji zmian — otwarcie modala synchronicznie wywracało się na
    // ExpressionChangedAfterItHasBeenChecked.
    setTimeout(() => {
      const modalRef = this.modal.open(component, CONFIRMATION_MODAL);
      const popUp: ConfirmationPopUp = modalRef.componentInstance;
      popUp.modalType = SINGLE_OK_BUTTON;
      popUp.message = message;
      popUp.eventId = eventId;

      modalRef.result.then(
        () => { closed.next(); closed.complete(); },
        // Drugi callback jest po to, żeby odrzucona obietnica nie trafiła do konsoli jako
        // nieobsłużony błąd — patrz CONFIRMATION_MODAL: tą ścieżką użytkownik nie przejdzie.
        () => closed.complete()
      );
    });

    return closed.asObservable();
  }
}
